import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { TextField, SortMenu, ButtonMenu, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";

const Filters = ({
  onSortChange,
  onOrderChange,
  selectedSortOption = "lastActive",
  orderDirection = "desc",
  onSearchChange,
  onRoleChange,
  onPermissionsChange,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRoleChange = useCallback((value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newRole = selectedRole === value ? null : value;
    setSelectedRole(newRole);
    onRoleChange?.(newRole);
  }, [onRoleChange, selectedRole]);

  const handlePermissionsChange = useCallback((value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newPermission = selectedPermission === value ? null : value;
    setSelectedPermission(newPermission);
    onPermissionsChange?.(newPermission);
  }, [onPermissionsChange, selectedPermission]);

  const handleClearFilters = useCallback(() => {
    setSelectedRole(null);
    setSelectedPermission(null);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onRoleChange?.(null);
    onPermissionsChange?.(null);
    onSearchChange?.("");
  }, [onRoleChange, onPermissionsChange, onSearchChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedRole !== null || selectedPermission !== null || searchValue.trim() !== "";
  }, [selectedRole, selectedPermission, searchValue]);

  // Role options for filter
  const roleOptions = [
    { label: "Admin", value: "Admin" },
    { label: "Approver", value: "Approver" },
    { label: "Contributor", value: "Contributor" },
  ];

  // Permission options for filter
  const permissionOptions = [
    { label: "View", value: "View" },
    { label: "Edit", value: "Edit" },
    { label: "Suggest", value: "Suggest" },
  ];

  // Get label for role filter button
  const getRoleLabel = () => {
    return selectedRole || "Role";
  };

  // Get label for permissions filter button
  const getPermissionsLabel = () => {
    return selectedPermission || "Permissions";
  };

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>

        <ButtonMenu
          key={`role-filter-${filtersKey}`}
          label={getRoleLabel()}
          menuPosition="bottom"
          onChange={handleRoleChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
        >
          <ButtonMenu.Menu>
            {roleOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>

        <ButtonMenu
          key={`permissions-filter-${filtersKey}`}
          label={getPermissionsLabel()}
          menuPosition="bottom"
          onChange={handlePermissionsChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
        >
          <ButtonMenu.Menu>
            {permissionOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>

        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>

      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={orderDirection ? orderDirection.toLowerCase() : "desc"}
          onOrderChange={(newOrder) => {
            if (onOrderChange) {
              onOrderChange(newOrder);
            }
          }}
          options={[
            {
              icon: 'sortByAlpha',
              id: 'name',
              label: 'Name'
            },
            {
              icon: 'email',
              id: 'email',
              label: 'Email'
            },
            {
              icon: 'clock',
              id: 'lastActive',
              label: 'Last Active'
            }
          ]}
          selected={selectedSortOption || "lastActive"}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
